/**
 * Panda CSS styles for HALO Button component
 *
 * Uses Class Variance Authority (cva) for systematic variant generation
 * Implements flat design with hover states and responsive fluid behavior
 */

import { css, cva } from '@/styled-system/css';

/**
 * Base button styles shared across all variants
 * Includes elevation shadow, transitions, and accessibility states
 */
export const buttonBase = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 'inherit',
  lineHeight: 1,

  // Accessibility and interaction states
  _disabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },

  _focus: {
    outlineWidth: '2px',
    outlineStyle: 'solid',
    outlineColor: 'hpxTeal500',
    outlineOffset: '2px',
  },
});

/**
 * Variant styles using Class Variance Authority (cva)
 * Generates all possible combinations systematically
 */
export const buttonVariants = cva({
  variants: {
    shape: {
      circle: {
        borderRadius: '50%',
        padding: '0',
        // Fixed dimensions, content centered
        flexShrink: 0,
      },
      rectangle: {
        borderRadius: '4px',
        paddingX: '16px',
        paddingY: '12px',
        height: '40px',
        minWidth: '80px',
        gap: '8px', // Space between icon and text
        flexDirection: 'row',
        whiteSpace: 'nowrap',
      },
    },

    tone: {
      brand: {}, // Colors defined in compound variants
      neutral: {},
    },

    emphasis: {
      filled: {}, // Background styles in compound variants
      outlined: {},
    },

    size: {
      sm: {}, // Dimensions defined in compound variants by shape
      md: {},
      lg: {},
      xl: {},
    },

    fluid: {
      true: {}, // Width behavior in compound variants
      false: {},
    },

    iconPosition: {
      start: {
        flexDirection: 'row',
      },
      end: {
        flexDirection: 'row-reverse',
      },
    },
  },

  compoundVariants: [
    // ==========================================
    // TONE + EMPHASIS COLOR COMBINATIONS
    // ==========================================

    // Brand + Filled (Primary CTAs)
    {
      tone: 'brand',
      emphasis: 'filled',
      css: {
        backgroundColor: 'hpxTeal500',
        color: 'hpxWhite',
        border: 'none',
        fontWeight: 'bold',

        _hover: {
          backgroundColor: 'hpxTeal600',
        },

        _active: {
          backgroundColor: 'hpxTeal700',
        },
      },
    },

    // Brand + Outlined (Secondary actions with brand color)
    {
      tone: 'brand',
      emphasis: 'outlined',
      css: {
        backgroundColor: 'hpxWhite',
        color: 'hpxTeal500',
        borderWidth: '0.533px',
        borderStyle: 'solid',
        borderColor: 'hpxTeal500',

        _hover: {
          backgroundColor: 'hpxTeal100',
        },

        _active: {
          backgroundColor: 'hpxTeal300',
        },
      },
    },

    // Neutral + Outlined (Tertiary/neutral actions)
    {
      tone: 'neutral',
      emphasis: 'outlined',
      css: {
        backgroundColor: 'hpxWhite',
        color: 'hpxGrey500',
        borderWidth: '0.75px',
        borderStyle: 'solid',
        borderColor: 'hpxGrey500',
        fontWeight: 'normal',

        _hover: {
          backgroundColor: 'hpxGrey100',
          borderColor: 'hpxGrey600',
          color: 'hpxGrey600',
        },

        _active: {
          backgroundColor: 'hpxGrey400',
        },
      },
    },

    // ==========================================
    // CIRCLE BUTTON SIZE VARIANTS
    // ==========================================

    {
      shape: 'circle',
      size: 'sm',
      css: {
        width: '32px',
        height: '32px',
        fontSize: '14px', // Icon size scaling
      },
    },
    {
      shape: 'circle',
      size: 'md',
      css: {
        width: '40px',
        height: '40px',
        fontSize: '16px',
      },
    },
    {
      shape: 'circle',
      size: 'lg',
      css: {
        width: '48px',
        height: '48px',
        fontSize: '20px',
      },
    },
    {
      shape: 'circle',
      size: 'xl',
      css: {
        width: '54px',
        height: '54px',
        fontSize: '22px',
      },
    },

    // ==========================================
    // RECTANGLE FLUID WIDTH VARIANTS
    // ==========================================

    {
      shape: 'rectangle',
      fluid: true,
      css: {
        width: '100%',
        justifyContent: 'center',
      },
    },
    {
      shape: 'rectangle',
      fluid: false,
      css: {
        width: 'auto',
        minWidth: '80px',
      },
    },

    // ==========================================
    // RECTANGLE SIZE VARIANTS (padding/text)
    // ==========================================

    {
      shape: 'rectangle',
      size: 'sm',
      css: {
        paddingX: '12px',
        paddingY: '8px',
        fontSize: '14px',
        height: '32px',
      },
    },
    {
      shape: 'rectangle',
      size: 'md',
      css: {
        paddingX: '16px',
        paddingY: '12px',
        fontSize: '16px',
        height: '40px',
      },
    },
    {
      shape: 'rectangle',
      size: 'lg',
      css: {
        paddingX: '20px',
        paddingY: '16px',
        fontSize: '18px',
        height: '48px',
      },
    },
    {
      shape: 'rectangle',
      size: 'xl',
      css: {
        paddingX: '24px',
        paddingY: '18px',
        fontSize: '20px',
        height: '54px',
      },
    },
  ],

  defaultVariants: {
    shape: 'rectangle',
    tone: 'brand',
    emphasis: 'filled',
    size: 'md',
    fluid: false,
    iconPosition: 'start',
  },
});

/**
 * Loading spinner styles
 */
export const loadingSpinner = css({
  width: '16px',
  height: '16px',
  borderWidth: '2px',
  borderStyle: 'solid',
  borderColor: 'transparent',
  borderTopWidth: '2px',
  borderTopStyle: 'solid',
  borderTopColor: 'currentColor',
  borderRadius: '50%',
  animationName: 'spin',
  animationDuration: '1s',
  animationTimingFunction: 'linear',
  animationIterationCount: 'infinite',
});

/**
 * Icon wrapper for proper spacing and alignment
 */
export const iconWrapper = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
});
